import React from 'react';
import { CheckIcon, MoreIcon, PlusIcon } from '@mentimeter/ragnar-visuals';
import type { ResponseText } from '@mentimeter/http-clients';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import {
  DropdownMenuContainer,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@mentimeter/ragnar-ui/menu';
import { Text } from '@mentimeter/ragnar-ui/text';
import { ThemeMentiLight } from '@mentimeter/ragnar-ui/themes';
import { useTranslations } from 'next-intl';
import { useHasSeenModerationOnboarding } from '../../utils/useHasSeenModerationOnboarding';
import { GroupModerationOnboarding } from './GroupModerationOnboarding';

export interface ModerationGroup {
  id: string;
  label: string;
  shape: React.ReactNode;
}

interface GroupModerationMenuProps {
  response: ResponseText;
  currentGroupId: string;
  moderationOptions: ModerationGroup[];
  onCreateNewGroup: (response: ResponseText) => void;
  onAssignToGroup: (targetGroupId: string, response: ResponseText) => void;
  showOnboardingEnabled?: boolean;
}

export const GroupModerationMenu = ({
  showOnboardingEnabled = false,
  currentGroupId,
  response,
  moderationOptions,
  onCreateNewGroup,
  onAssignToGroup,
}: GroupModerationMenuProps) => {
  const translate = useTranslations('presenting');

  const [hasSeenOnboarding, setHasSeenOnboarding] =
    useHasSeenModerationOnboarding();

  const [isShowingOnboarding, setIsShowingOnboarding] = React.useState(false);

  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (showOnboardingEnabled && !hasSeenOnboarding) {
      // Show onboarding after 1 second
      timeoutId = setTimeout(() => {
        setIsShowingOnboarding(true);
      }, 1000);
    }
    return () => clearTimeout(timeoutId); // Clean up on unmount
  }, [showOnboardingEnabled, hasSeenOnboarding]);

  const handleDismiss = () => {
    if (isShowingOnboarding) {
      setIsShowingOnboarding(false);
      setHasSeenOnboarding();
    }
  };

  if (showOnboardingEnabled && !hasSeenOnboarding) {
    return (
      <GroupModerationOnboarding
        isShowing={isShowingOnboarding}
        onDismiss={handleDismiss}
      />
    );
  }

  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger>
        <Clickable
          aria-label={translate(
            'grouping_responses.open_group_moderation_menu_a11y',
            { groupName: response.id },
          )}
          p={1}
        >
          <MoreIcon size={2} color="text" />
        </Clickable>
      </DropdownMenuTrigger>
      <ThemeMentiLight>
        <DropdownMenuContainer align="start">
          {moderationOptions.map((group) => (
            <DropdownMenuItem
              key={group.id}
              disabled={currentGroupId === group.id}
              onClick={() => onAssignToGroup(group.id, response)}
            >
              <Box width="10px">
                {currentGroupId === group.id && (
                  <CheckIcon size={2} color="text" />
                )}
              </Box>
              <Box width="24px">{group.shape}</Box>
              <Text>{group.label}</Text>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onCreateNewGroup(response)}>
            <PlusIcon size={2} color="text" />
            <Text color="text">{translate('legacy.clustering.add_group')}</Text>
          </DropdownMenuItem>
        </DropdownMenuContainer>
      </ThemeMentiLight>
    </DropdownMenuRoot>
  );
};
